import { ArrowDown, ArrowUp, CheckCircle2, Database, XCircle } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";

import type { PipelineSurfaceStatus } from "@busright/data-contract";

import { ChartCard } from "@/components/ChartCard";
import { type DsCadence, dsFreshnessColor, dsFreshnessLabel } from "@/components/DsFreshnessNode";
import { EmptyState } from "@/components/EmptyState";
import { formatInt, relativeFromNow } from "@/lib/format";

// Which dataset a curated surface lives in + its EXPECTED refresh cadence. Cadence is what makes
// the freshness reading meaningful: a daily rollup that is a day old is healthy; a continuous
// stream that is a day old is not. Surfaces not listed fall back to "reporting" / "daily".
interface SurfaceMeta {
  readonly dataset: string;
  readonly cadence: DsCadence;
}
const SURFACE_META: Record<string, SurfaceMeta> = {
  dd_location_pings: { dataset: "hardware_health", cadence: "continuous" },
  device_daily_hw_rollup: { dataset: "hardware_health", cadence: "daily" },
  esper_device_status: { dataset: "hardware_health", cadence: "continuous" },
  esper_device_snapshot: { dataset: "hardware_health", cadence: "daily" },
  route_session_hw_enriched: { dataset: "hardware_health", cadence: "continuous" },
  rpt_hw_device_latest: { dataset: "reporting", cadence: "daily" },
  rpt_hw_district_latest: { dataset: "reporting", cadence: "daily" },
  account_map: { dataset: "reporting", cadence: "monthly" },
  stg_e2_orders: { dataset: "supply_chain", cadence: "daily" },
  stg_e2_shipments: { dataset: "supply_chain", cadence: "daily" },
  stg_e2_invoices: { dataset: "supply_chain", cadence: "monthly" },
  stg_e2_rmas: { dataset: "supply_chain", cadence: "daily" },
  stg_e2_inventory: { dataset: "supply_chain", cadence: "daily" },
};
const datasetFor = (surface: string): string => SURFACE_META[surface]?.dataset ?? "reporting";
const cadenceFor = (surface: string): DsCadence => SURFACE_META[surface]?.cadence ?? "daily";

interface Row extends PipelineSurfaceStatus {
  readonly dataset: string;
  readonly cadence: DsCadence;
}

type SortKey = "surface" | "dataset" | "rowCount" | "latestDate" | "staleness";
type SortDir = "asc" | "desc";

// FRESH is best (0) → STALE is worst (2): a numeric rank so the status column sorts meaningfully.
// Unknown values rank after every known status.
const STALENESS_RANK: Record<string, number> = { FRESH: 0, RECENT: 1, STALE: 2 };
const STALENESS_RANK_UNKNOWN = 3;
const stalenessRank = (value: string): number => STALENESS_RANK[value] ?? STALENESS_RANK_UNKNOWN;

const compareRows = (a: Row, b: Row, key: SortKey): number => {
  if (key === "rowCount") {
    return a.rowCount - b.rowCount;
  }
  if (key === "staleness") {
    return stalenessRank(a.staleness) - stalenessRank(b.staleness);
  }
  if (key === "latestDate") {
    return (a.latestDate ?? "").localeCompare(b.latestDate ?? "");
  }
  if (key === "dataset") {
    return a.dataset.localeCompare(b.dataset) || a.surface.localeCompare(b.surface);
  }
  return a.surface.localeCompare(b.surface);
};

// `key: null` = a column that is shown but not sortable (cadence is metadata, not a measure).
interface Header {
  readonly id: string;
  readonly key: SortKey | null;
  readonly label: string;
  readonly num?: boolean;
}
const HEADERS: readonly Header[] = [
  { id: "surface", key: "surface", label: "Source table" },
  { id: "dataset", key: "dataset", label: "Dataset" },
  { id: "cadence", key: null, label: "Cadence" },
  { id: "rowCount", key: "rowCount", label: "Rows", num: true },
  { id: "latestDate", key: "latestDate", label: "Last refresh" },
  { id: "staleness", key: "staleness", label: "Status" },
];

interface ChipProps {
  readonly icon: typeof Database;
  readonly label: string;
  readonly value: number;
  readonly color?: string;
}
const Chip = ({ icon: Icon, label, value, color }: ChipProps): ReactNode => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 14px",
      background: "var(--br-surface-2)",
      border: "1px solid var(--br-border)",
      borderRadius: "var(--br-radius-md)",
    }}
  >
    <Icon size={16} aria-hidden style={{ color: color ?? "var(--br-text-secondary)" }} />
    <span
      className="br-tabular-nums"
      style={{ fontSize: 18, fontWeight: 700, color: color ?? "var(--br-text-primary)" }}
    >
      {value}
    </span>
    <span style={{ fontSize: 12, color: "var(--br-text-muted)" }}>{label}</span>
  </div>
);

// Sort direction caret for an active column header (null when the column isn't the active sort).
const SortIcon = ({
  active,
  dir,
}: {
  readonly active: boolean;
  readonly dir: SortDir;
}): ReactNode => {
  if (!active) {
    return null;
  }
  return dir === "asc" ? <ArrowUp size={11} aria-hidden /> : <ArrowDown size={11} aria-hidden />;
};

const StatusPill = ({ staleness }: { readonly staleness: string }): ReactNode => {
  const fresh = staleness === "FRESH" || staleness === "RECENT" || staleness === "STALE";
  const value = fresh ? (staleness as "FRESH" | "RECENT" | "STALE") : "UNKNOWN";
  const color = dsFreshnessColor(value);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 9px",
        fontSize: 11,
        fontWeight: 600,
        color,
        borderRadius: 999,
        border: `1px solid ${color}`,
      }}
    >
      <span
        aria-hidden
        style={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }}
      />
      {dsFreshnessLabel(value)}
    </span>
  );
};

export interface DsPipelineHealthProps {
  readonly surfaces: readonly PipelineSurfaceStatus[];
  readonly accountMatchRatePct: number;
  readonly loading?: boolean;
  // Click a row to focus the raw explorer on that source table. Optional — rows are static if absent.
  readonly onFocusTable?: (table: string) => void;
}

// Section 1 — every source table, crystal clear. Summary chips (sources / fresh / stale-or-broken)
// over a sortable grid of dataset · table · row count · last refresh · cadence-aware status.
export const DsPipelineHealth = ({
  surfaces,
  accountMatchRatePct,
  loading,
  onFocusTable,
}: DsPipelineHealthProps): ReactNode => {
  const [sortKey, setSortKey] = useState<SortKey>("staleness");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const rows = useMemo<readonly Row[]>(() => {
    const decorated = surfaces.map<Row>((s) => ({
      ...s,
      dataset: datasetFor(s.surface),
      cadence: cadenceFor(s.surface),
    }));
    const sorted = [...decorated].sort((a, b) => compareRows(a, b, sortKey));
    return sortDir === "asc" ? sorted : sorted.reverse();
  }, [surfaces, sortKey, sortDir]);

  const counts = useMemo(() => {
    let fresh = 0;
    let staleOrBroken = 0;
    for (const s of surfaces) {
      if (s.staleness === "FRESH") {
        fresh += 1;
      } else {
        staleOrBroken += 1;
      }
    }
    return { total: surfaces.length, fresh, staleOrBroken };
  }, [surfaces]);

  const onSort = (key: SortKey): void => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "surface" || key === "dataset" ? "asc" : "desc");
  };

  return (
    <ChartCard
      title="Pipeline health"
      subtitle="Every curated source table, its freshness, and how it should refresh"
      source={`account match ${accountMatchRatePct.toFixed(1)}%`}
      loading={loading}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        <Chip icon={Database} label="sources" value={counts.total} />
        <Chip
          icon={CheckCircle2}
          label="fresh"
          value={counts.fresh}
          color={dsFreshnessColor("FRESH")}
        />
        <Chip
          icon={XCircle}
          label="stale / broken"
          value={counts.staleOrBroken}
          color={counts.staleOrBroken > 0 ? dsFreshnessColor("STALE") : undefined}
        />
      </div>

      {rows.length === 0 ? (
        <EmptyState title="No source surfaces" message="Pipeline health returned no surfaces." />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="data-table">
            <thead>
              <tr>
                {HEADERS.map((h) => {
                  if (h.key === null) {
                    return (
                      <th key={h.id} className={h.num ? "num" : undefined}>
                        {h.label}
                      </th>
                    );
                  }
                  const colKey = h.key;
                  const active = colKey === sortKey;
                  return (
                    <th key={h.id} className={h.num ? "num" : undefined}>
                      <button
                        type="button"
                        onClick={() => onSort(colKey)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          padding: 0,
                          font: "inherit",
                          color: active ? "var(--br-text-primary)" : "inherit",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        {h.label}
                        <SortIcon active={active} dir={sortDir} />
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const selectable = typeof onFocusTable === "function";
                return (
                  <tr
                    key={row.surface}
                    className={selectable ? "ds-row-focusable" : undefined}
                    onClick={selectable ? () => onFocusTable?.(row.surface) : undefined}
                  >
                    <td className="mono" title={row.note ?? undefined}>
                      {row.surface}
                    </td>
                    <td>{row.dataset}</td>
                    <td>{row.cadence}</td>
                    <td className="num br-tabular-nums">{formatInt(row.rowCount)}</td>
                    <td className="br-tabular-nums">
                      {row.latestDate ?? "—"}
                      {row.latestDate ? (
                        <span style={{ color: "var(--br-text-muted)", marginLeft: 6 }}>
                          ({relativeFromNow(row.latestDate)})
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <StatusPill staleness={row.staleness} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </ChartCard>
  );
};
